// «La telefonata col quaderno»: mamma parla e ascolta senza premere nulla.
// Macchina a stati: pronta → apertura (l'app saluta) → ascolto → penso → parlo → ascolto …
// → riaggancio → chiusa. Un solo getUserMedia e un solo AudioContext per tutta la chiamata
// (creati nel tocco su «Chiama»), un MediaRecorder nuovo per turno (stesso upload a chunk
// del dialogo: l'audio di ogni turno resta salvato come oggi).
//
// PARAMETRI VAD — da tarare sull'iPad vero con la telemetria (admin › Prova › Ultime telefonate):
export const CALL = {
  floorMs: 500,          // rumore di fondo stimato nei primi 500 ms di ogni ascolto (mediana)
  thresholdMin: 0.02,    // soglia RMS minima (la stessa del dialogo)
  floorFactor: 2.5,      // soglia = max(thresholdMin, 2.5 × rumore di fondo)
  minSpeechMs: 600,      // parlato valido: almeno 600 ms cumulativi sopra soglia (no colpi, TV)
  silenceCloseMs: 3000,  // 3 s di silenzio DOPO il parlato → si chiude il turno e si manda
  noSpeechMs: 12000,     // nessun parlato per 12 s → «Ci sei? Ti ascolto.»; al secondo giro saluta
  maxTurnMs: 90000,      // tetto per turno: si chiude e si manda comunque
  restartDelayMs: 400,   // pausa tra la fine dell'ultimo audio e il nuovo ascolto (coda di riverbero)
  tickMs: 100,           // periodo del misuratore
  maxEmptyTurns: 3,      // turni «vuoto» consecutivi → riaggancio gentile
  maxBusyTurns: 3,       // turni «occupato» consecutivi → riaggancio gentile
  maxErrorTurns: 3,      // errori di rete/STT consecutivi → riaggancio gentile
  busyRetryMs: 4000,     // attesa prima di riascoltare dopo un «occupato»
  sseTimeoutMs: 120000,  // intera risposta di un turno (lock 25 s + whisper + Claude 60 s + TTS)
  saveOnlyTimeoutMs: 45000,   // «Riattacca» mentre parlava: trascrizione delle ultime parole
  transcriptWaitMs: 30000,    // «Riattacca» durante «penso»: attesa della trascrizione prima di chiudere
} as const;

import { api, postSse } from './api.js';
import { deleteRecording } from './idb.js';
import { finishRecording, Recorder } from './recorder.js';
import { contaVoce, playQueue, speakLocal, stopSpeaking, waitQueue } from './tts.js';
import { taccuino } from './appunti.js';
import { el, fmtTimer } from './ui.js';

export type CallState = 'pronta' | 'apertura' | 'ascolto' | 'penso' | 'parlo' | 'riaggancio' | 'chiusa';

export type CallDeps = {
  caro: string;
  /** Frase locale a voce (TTS del server o dell'iPad): «Ci sei? Ti ascolto.» */
  say: (text: string) => Promise<void>;
  /** Fine della telefonata: memoryId se la chiacchierata è diventata un ricordo. */
  onEnd: (memoryId: number | undefined) => void;
};

type CallUi = {
  state(s: CallState): void;
  level(l: number): void;
  app(text: string): void;
  mamma(text: string): void;
  /** Gli appunti che si vedono: quello che il quaderno ha capito finora. */
  appunti?(d: Record<string, unknown>): void;
  note(text: string): void;
};

type Telemetry = {
  reason: string;
  speechMs?: number;
  silenceMs?: number;
  uploadMs?: number;
  sttMs?: number;
  claudeMs?: number;
  ttsMs?: number;
  levelFloor?: number;
  levelPeak?: number;
};

type TurnOutcome = {
  errCode: string | null;
  text: string;
  end: boolean;
  audioCount: number;
  sttMs?: number;
  claudeMs?: number;
  ttsMs?: number;
};

const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

async function until(cond: () => boolean, timeoutMs: number): Promise<void> {
  const start = Date.now();
  while (!cond() && Date.now() - start < timeoutMs) await delay(200);
}

export class Call {
  state: CallState = 'pronta';
  private sessionId: number | null = null;
  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private wakeLock: WakeLockSentinel | null = null;
  private rec: Recorder | null = null;
  private ended = false;
  private closing = false;
  private turnStartedAt = 0;
  private turnIndex = 0;
  private emptyStreak = 0;
  private busyStreak = 0;
  private errorStreak = 0;
  private noSpeechStreak = 0;
  // Turno in volo (upload + risposta): «Riattacca» aspetta la trascrizione prima di chiudere.
  private turnInFlight = false;
  private transcriptArrived = false;
  private turnAbort: AbortController | null = null;
  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'hidden') void this.hangup('sfondo');
    else if (!this.ended) void this.acquireWakeLock();
  };

  constructor(private deps: CallDeps, private ui: CallUi) {}

  /** Da chiamare DENTRO il tocco su «Chiama»: AudioContext e microfono nascono nel gesto. */
  async start(): Promise<void> {
    try {
      this.ctx = new AudioContext();
      void this.ctx.resume().catch(() => undefined);
    } catch { this.ctx = null; }
    void this.acquireWakeLock();
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch {
      // Niente sessione ancora: si resta sulla schermata con «Chiama», per riprovare.
      this.releaseHardware();
      this.ui.note(`Non riesco a usare il microfono. Controlla i permessi insieme a ${this.deps.caro}.`);
      this.setState('pronta');
      return;
    }
    for (const t of this.stream.getTracks()) t.onended = () => void this.hangup('microfono');
    try {
      const { sessionId } = await api.post<{ sessionId: number }>('/api/sessions', { mode: 'dialogo', handsFree: true });
      this.sessionId = sessionId;
    } catch {
      this.releaseHardware();
      this.ui.note('La rete ha fatto i capricci: riprova tra un momento.');
      this.setState('pronta');
      return;
    }
    this.ui.note('');
    document.addEventListener('visibilitychange', this.onVisibility);
    await this.opener();
    if (!this.ended) await this.listen();
  }

  /** «Ho finito di parlare»: rete di sicurezza se il VAD non chiude. */
  finishSpeaking(): void {
    if (this.state === 'ascolto') void this.closeTurn('manuale');
  }

  private setState(s: CallState): void {
    if (this.ended && s !== 'riaggancio' && s !== 'chiusa') return;
    this.state = s;
    this.ui.state(s);
  }

  private async acquireWakeLock(): Promise<void> {
    try {
      if ('wakeLock' in navigator) this.wakeLock = await navigator.wakeLock.request('screen');
    } catch { this.wakeLock = null; }
  }

  private telemetry(t: Telemetry, nuovoTurno = true): void {
    if (this.sessionId === null) return;
    const body = { sessionId: this.sessionId, turnIndex: nuovoTurno ? this.turnIndex++ : Math.max(0, this.turnIndex - 1), ...t };
    void fetch('/api/conversation/telemetry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => undefined);
  }

  /** Aspetta che la voce del quaderno finisca. Se nessun file si è sentito (su iPad il file può
   *  non partire o spegnersi quando si apre il microfono), rilegge il testo con la voce dell'iPad:
   *  prima restava in silenzio, e lei vedeva solo le parole scritte. Ogni volta lo annota. */
  private async ascoltaVoce(text: string): Promise<void> {
    this.setState('parlo');
    await waitQueue();
    const { sentiti, muti } = contaVoce();
    if (sentiti + muti > 0) this.telemetry({ reason: sentiti === 0 ? 'voce/muta' : muti > 0 ? 'voce/in_parte' : 'voce/ok' }, false);
    // Direttamente la voce dell'iPad: `say` riproverebbe un file del server, che può restare muto allo stesso modo.
    if (sentiti === 0 && text && !this.ended) {
      this.ui.app(text);
      await speakLocal(text);
    }
  }

  /** Frase locale: si sente e si legge. */
  private async speak(text: string): Promise<void> {
    if (this.ended) return;
    this.setState('parlo');
    this.ui.app(text);
    await this.deps.say(text).catch(() => undefined);
  }

  // --- apertura: l'app saluta per prima --------------------------------------
  private async opener(): Promise<void> {
    if (this.ended || this.sessionId === null) return;
    this.setState('apertura');
    contaVoce(); // si conta da qui: niente resti di prima
    let text = '';
    let audioCount = 0;
    this.turnAbort = new AbortController();
    try {
      await postSse('/api/conversation/opener', { sessionId: this.sessionId }, {
        delta: (d) => { text += String(d['text'] ?? ''); this.ui.app(text); },
        audio: (d) => {
          const url = String(d['url'] ?? '');
          if (!url) return;
          audioCount++;
          this.setState('parlo');
          void playQueue(url);
        },
        done: (d) => { text = String(d['text'] ?? text); this.ui.app(text); },
        error: () => { /* fallback sotto */ },
      }, { signal: this.turnAbort.signal, timeoutMs: CALL.sseTimeoutMs });
    } catch { /* fallback sotto */ }
    if (this.ended) return;
    if (!text) {
      text = 'Eccomi! Raccontami quello che vuoi: ti ascolto.';
      audioCount = 0;
    }
    if (audioCount === 0) await this.speak(text);
    else await this.ascoltaVoce(text);
    await delay(CALL.restartDelayMs);
  }

  // --- ascolto -----------------------------------------------------------------
  private async listen(): Promise<void> {
    if (this.ended || !this.stream) return;
    this.closing = false;
    this.transcriptArrived = false;
    this.setState('ascolto');
    this.ui.mamma('');
    const rec = new Recorder({
      onLevel: (l) => { if (this.state === 'ascolto') this.ui.level(l); },
      onTick: () => this.checkTimers(rec),
      onSilence: (s) => { if (this.rec === rec && s * 1000 >= CALL.silenceCloseMs) void this.closeTurn('silenzio'); },
      onError: (m) => this.ui.note(m),
    }, {
      stream: this.stream,
      ...(this.ctx ? { ctx: this.ctx } : {}),
      vad: {
        threshold: CALL.thresholdMin,
        adaptiveFloor: true,
        floorFactor: CALL.floorFactor,
        floorMs: CALL.floorMs,
        minSpeechMs: CALL.minSpeechMs,
        tickMs: CALL.tickMs,
      },
    });
    this.turnStartedAt = Date.now();
    try {
      await rec.start({ purpose: 'turn', sessionId: this.sessionId ?? undefined });
    } catch {
      this.ui.note('La rete ha fatto i capricci: riprovo tra un momento.');
      if (++this.errorStreak >= CALL.maxErrorTurns) return this.hangup('errore');
      await delay(CALL.busyRetryMs);
      return this.listen();
    }
    if (this.ended) {
      await rec.discard();
      return;
    }
    this.rec = rec;
  }

  private checkTimers(rec: Recorder): void {
    if (this.rec !== rec) return;
    const elapsed = Date.now() - this.turnStartedAt;
    if (!rec.speechDetected && elapsed >= CALL.noSpeechMs) void this.closeTurn('nessun_parlato');
    else if (elapsed >= CALL.maxTurnMs) void this.closeTurn('cap');
  }

  // --- chiusura del turno: upload → trascrizione → risposta → voce → riascolto ----
  private async closeTurn(reason: string): Promise<void> {
    const rec = this.rec;
    if (!rec || this.closing) return;
    this.closing = true;
    this.rec = null;
    this.ui.level(0);
    const stats = rec.vadStats();
    const elapsedMs = Date.now() - this.turnStartedAt;
    const base = { levelFloor: stats.floor, levelPeak: stats.peak, speechMs: stats.speechMs };

    // Nessun parlato: niente upload, si chiede se c'è; al secondo giro si saluta.
    if (!rec.speechDetected && reason !== 'manuale') {
      await rec.discard();
      this.telemetry({ reason: 'nessun_parlato', silenceMs: elapsedMs, ...base });
      if (this.ended) return;
      this.noSpeechStreak++;
      if (this.noSpeechStreak >= 2) {
        await this.speak('Allora ci sentiamo un\'altra volta. A presto!');
        return this.hangup('silenzio');
      }
      await this.speak('Ci sei? Ti ascolto.');
      await delay(CALL.restartDelayMs);
      return this.listen();
    }

    this.turnInFlight = true;
    this.setState('penso');
    const t0 = Date.now();
    let mediaId: number;
    try {
      const { recordingId, durationS, chunkCount } = await rec.stop();
      if (durationS < 1) {
        void deleteRecording(recordingId);
        this.turnInFlight = false;
        this.telemetry({ reason: `${reason}/corto`, silenceMs: CALL.silenceCloseMs, ...base });
        return this.afterTurn({ errCode: 'vuoto', text: '', end: false, audioCount: 0 });
      }
      const fin = await finishRecording<{ mediaId: number }>(recordingId, {
        sessionId: this.sessionId,
        durationS,
        expectedChunks: chunkCount,
      });
      void deleteRecording(recordingId);
      mediaId = fin.mediaId;
    } catch {
      this.turnInFlight = false;
      this.telemetry({ reason: `${reason}/rete`, uploadMs: Date.now() - t0, ...base });
      return this.afterTurn({ errCode: 'rete', text: '', end: false, audioCount: 0 });
    }
    const uploadMs = Date.now() - t0;

    // Se nel frattempo ha riattaccato, exchange manda saveOnly: le parole si salvano lo stesso.
    const out = await this.exchange(mediaId);
    this.turnInFlight = false;
    this.telemetry({
      reason: out.errCode ? `${reason}/${out.errCode}` : reason,
      silenceMs: reason === 'silenzio' ? CALL.silenceCloseMs : undefined,
      uploadMs,
      sttMs: out.sttMs,
      claudeMs: out.claudeMs,
      ttsMs: out.ttsMs,
      ...base,
    });
    return this.afterTurn(out);
  }

  /** Invia il turno e riproduce la risposta man mano che arriva (una frase alla volta). */
  private async exchange(mediaId: number): Promise<TurnOutcome> {
    const out: TurnOutcome = { errCode: null, text: '', end: false, audioCount: 0 };
    if (this.sessionId === null) { out.errCode = 'interno'; return out; }
    const t0 = Date.now();
    let transcriptAt = 0;
    let firstDeltaAt = 0;
    let firstAudioAt = 0;
    this.turnAbort = new AbortController();
    // Se nel frattempo ha riattaccato, si salvano solo le sue parole (niente risposta).
    const saveOnly = this.ended;
    contaVoce(); // si conta da qui: niente resti del turno prima
    try {
      await postSse('/api/conversation/turn', { sessionId: this.sessionId, mediaId, ...(saveOnly ? { saveOnly: true } : {}) }, {
        transcript: (d) => {
          transcriptAt = Date.now();
          this.transcriptArrived = true;
          this.ui.mamma(String(d['text'] ?? ''));
        },
        appunti: (d) => { this.ui.appunti?.(d); },
        delta: (d) => {
          if (!firstDeltaAt) firstDeltaAt = Date.now();
          out.text += String(d['text'] ?? '');
          this.ui.app(out.text);
        },
        audio: (d) => {
          const url = String(d['url'] ?? '');
          if (!url || this.ended) return;
          if (!firstAudioAt) firstAudioAt = Date.now();
          out.audioCount++;
          this.setState('parlo');
          void playQueue(url);
        },
        error: (d) => { out.errCode = String(d['code'] ?? 'interno'); },
        done: (d) => {
          out.text = String(d['text'] ?? out.text);
          out.end = d['end'] === true;
          this.ui.app(out.text);
        },
      }, { signal: this.turnAbort.signal, timeoutMs: saveOnly ? CALL.saveOnlyTimeoutMs : CALL.sseTimeoutMs });
    } catch {
      if (!this.ended) out.errCode = out.errCode ?? 'rete';
    }
    const doneAt = Date.now();
    if (transcriptAt) out.sttMs = transcriptAt - t0;
    if (transcriptAt && !out.errCode) out.claudeMs = doneAt - transcriptAt;
    if (firstAudioAt && firstDeltaAt) out.ttsMs = firstAudioAt - firstDeltaAt;
    return out;
  }

  private async afterTurn(out: TurnOutcome): Promise<void> {
    if (this.ended) return;
    switch (out.errCode) {
      case null: {
        this.emptyStreak = 0;
        this.busyStreak = 0;
        this.errorStreak = 0;
        this.noSpeechStreak = 0;
        if (out.audioCount === 0 && out.text) await this.speak(out.text);
        else await this.ascoltaVoce(out.text);
        if (out.end) return this.hangup('saluto');
        break;
      }
      case 'vuoto': {
        if (++this.emptyStreak >= CALL.maxEmptyTurns) {
          await this.speak('Non riesco a sentirti bene. Riproviamo un\'altra volta: a presto!');
          return this.hangup('vuoti');
        }
        await this.speak('Non ti ho sentita bene, ripeti pure.');
        break;
      }
      case 'occupato': {
        if (++this.busyStreak >= CALL.maxBusyTurns) {
          await this.speak('Il quaderno è molto occupato adesso. La tua voce è salvata: riproviamo tra un po\'.');
          return this.hangup('occupato');
        }
        await this.speak('Il quaderno è occupato un momento, aspetta.');
        await delay(CALL.busyRetryMs);
        break;
      }
      default: {
        if (++this.errorStreak >= CALL.maxErrorTurns) {
          await this.speak('C\'è qualche problema con la linea. La tua voce è salvata: ci risentiamo più tardi.');
          return this.hangup('errore');
        }
        await this.speak('La linea ha fatto i capricci, ma la tua voce è salvata. Dimmi pure.');
      }
    }
    if (this.ended) return;
    await delay(CALL.restartDelayMs);
    return this.listen();
  }

  // --- riaggancio ----------------------------------------------------------------
  /** «Riattacca», saluto, silenzio, sfondo: mette in salvo l'ultimo turno e chiude la sessione. */
  async hangup(reason: string): Promise<void> {
    if (this.ended) return;
    this.ended = true;
    this.setState('riaggancio');
    stopSpeaking();
    document.removeEventListener('visibilitychange', this.onVisibility);
    try {
      const rec = this.rec;
      this.rec = null;
      if (rec) {
        // Stava ascoltando: le ultime parole si trascrivono e si salvano, senza risposta.
        if (rec.speechDetected && this.sessionId !== null) {
          const { recordingId, durationS, chunkCount } = await rec.stop();
          if (durationS >= 1) {
            const { mediaId } = await finishRecording<{ mediaId: number }>(recordingId, {
              sessionId: this.sessionId,
              durationS,
              expectedChunks: chunkCount,
            });
            void deleteRecording(recordingId);
            const st = rec.vadStats();
            this.telemetry({ reason: `${reason}/salvato`, speechMs: st.speechMs, levelFloor: st.floor, levelPeak: st.peak });
            await postSse('/api/conversation/turn', { sessionId: this.sessionId, mediaId, saveOnly: true }, {}, { timeoutMs: CALL.saveOnlyTimeoutMs }).catch(() => undefined);
          } else {
            void deleteRecording(recordingId);
          }
        } else {
          await rec.discard();
        }
      } else if (this.turnInFlight) {
        // «Penso»: le sue parole entrano in DB con la trascrizione; poi Claude si interrompe.
        await until(() => this.transcriptArrived || !this.turnInFlight, CALL.transcriptWaitMs);
        this.turnAbort?.abort();
      } else {
        this.turnAbort?.abort();
      }
    } catch { /* lo sweeper del server recupera */ }
    this.releaseHardware();
    let memoryId: number | undefined;
    if (this.sessionId !== null) {
      const res = await api.post<{ ok: boolean; memoryId?: number }>(`/api/sessions/${this.sessionId}/end`).catch(() => null);
      memoryId = res?.memoryId;
    }
    this.setState('chiusa');
    this.deps.onEnd(memoryId);
  }

  /** Uscita brusca (cambio schermata, pagehide): come il dialogo, turno orfano + end keepalive. */
  abandon(): void {
    if (this.ended) return;
    this.ended = true;
    stopSpeaking();
    document.removeEventListener('visibilitychange', this.onVisibility);
    const rec = this.rec;
    this.rec = null;
    this.turnAbort?.abort();
    const sessionId = this.sessionId;
    void (async () => {
      try {
        if (rec && !rec.stopped) {
          if (rec.speechDetected) {
            const r = await rec.stop();
            if (r.durationS >= 2 && sessionId !== null) {
              await finishRecording(r.recordingId, { sessionId, durationS: r.durationS, expectedChunks: r.chunkCount, orphanTurn: true });
            }
          } else {
            await rec.discard();
          }
        }
      } catch { /* lo sweeper del server recupera */ }
      try {
        if (sessionId !== null) await fetch(`/api/sessions/${sessionId}/end`, { method: 'POST', keepalive: true });
      } catch { /* idem */ }
    })();
    this.releaseHardware();
  }

  private releaseHardware(): void {
    this.stream?.getTracks().forEach((t) => { t.onended = null; t.stop(); });
    this.stream = null;
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    void this.wakeLock?.release().catch(() => undefined);
    this.wakeLock = null;
  }
}

/* ---------- schermata ----------
   Un cerchio grande a tre stati, la didascalia di ciò che dice l'app (per chi sente poco),
   la trascrizione di mamma, il timer, UN solo tasto rosso «Riattacca» (+ «Ho finito di
   parlare» come rete di sicurezza durante l'ascolto). */

const LABELS: Record<CallState, string> = {
  pronta: 'Pronta',
  apertura: 'Ti chiamo…',
  ascolto: 'Ti ascolto',
  penso: 'Ci penso…',
  parlo: 'Ti parlo',
  riaggancio: 'Un momento…',
  chiusa: 'A presto',
};

const INTRO_KEY = 'memorie_tel_intro';

export function buildCallScreen(deps: CallDeps): { root: HTMLElement; abandon: () => void } {
  const statoTesto = el('span', { class: 'tel-stato' }, '📞');
  const anello = el('div', { class: 'tel-anello', 'aria-hidden': 'true' });
  const cerchio = el('div', { class: 'tel-cerchio tel-pronta', role: 'status' }, anello, statoTesto);
  const didascalia = el('p', { class: 'tel-didascalia', 'aria-live': 'polite' }, 'Quando sei pronta, tocca «Chiama».');
  const mammaEl = el('p', { class: 'tel-mamma', 'aria-live': 'off' }, '');
  const timerEl = el('p', { class: 'tel-timer', 'aria-hidden': 'true' }, '');
  const notaEl = el('p', { class: 'tel-nota', role: 'status' }, '');
  const appunti = taccuino();

  const introTesto = 'Parla quando il cerchio è verde; quando hai finito, taci un momento e il quaderno ti risponde. Per chiudere tocca «Riattacca» o di’ «ciao, a domani».';
  let primaVolta = true;
  try { primaVolta = !localStorage.getItem(INTRO_KEY); } catch { /* ok */ }
  const intro = primaVolta
    ? el('div', { class: 'avviso tel-intro' }, introTesto)
    : el('p', { class: 'tel-nota' }, introTesto);

  const chiamaBtn = el('button', { class: 'tel-chiama' }, '📞  Chiama') as HTMLButtonElement;
  const riattaccaBtn = el('button', { class: 'tel-riattacca' }, '📵  Riattacca') as HTMLButtonElement;
  const finitoBtn = el('button', { class: 'btn-secondario tel-finito' }, 'Ho finito di parlare') as HTMLButtonElement;
  riattaccaBtn.hidden = true;
  finitoBtn.hidden = true;

  let timer: number | null = null;
  const stopTimer = (): void => { if (timer !== null) window.clearInterval(timer); timer = null; };

  const ui: CallUi = {
    state(s) {
      cerchio.className = `tel-cerchio tel-${s}`;
      statoTesto.textContent = s === 'pronta' ? '📞' : LABELS[s];
      cerchio.setAttribute('aria-label', LABELS[s]);
      chiamaBtn.hidden = s !== 'pronta';
      if (s === 'pronta') chiamaBtn.disabled = false;
      riattaccaBtn.hidden = s === 'pronta';
      finitoBtn.hidden = s !== 'ascolto';
      riattaccaBtn.disabled = s === 'riaggancio' || s === 'chiusa';
      if (s === 'riaggancio') riattaccaBtn.textContent = 'Un momento…';
      if (s !== 'ascolto') anello.style.setProperty('--liv', '0');
      if (s === 'chiusa' || s === 'pronta') stopTimer();
      if (s === 'pronta') timerEl.textContent = '';
    },
    level(l) { anello.style.setProperty('--liv', String(Math.min(1, l))); },
    app(text) { didascalia.textContent = text; },
    mamma(text) { mammaEl.textContent = text ? `Tu: ${text}` : ''; },
    appunti(d) { appunti.aggiorna(d); },
    note(text) { notaEl.textContent = text; },
  };

  const call = new Call(deps, ui);

  chiamaBtn.addEventListener('click', () => {
    if (chiamaBtn.disabled) return;
    chiamaBtn.disabled = true; // un tocco = una telefonata; si riabilita solo tornando in «pronta»
    intro.hidden = true;
    notaEl.textContent = '';
    try { localStorage.setItem(INTRO_KEY, '1'); } catch { /* ok */ }
    const startedAt = Date.now();
    timerEl.textContent = '0:00';
    timer = window.setInterval(() => { timerEl.textContent = fmtTimer(Math.floor((Date.now() - startedAt) / 1000)); }, 1000);
    didascalia.textContent = 'Un momento, ti chiamo…';
    void call.start();
  });
  riattaccaBtn.addEventListener('click', () => { void call.hangup('riattacca'); });
  finitoBtn.addEventListener('click', () => call.finishSpeaking());

  const root = el('div', { class: 'tel' },
    cerchio,
    didascalia,
    mammaEl,
    appunti.element,
    timerEl,
    notaEl,
    el('div', { class: 'tel-azioni' }, finitoBtn, chiamaBtn, riattaccaBtn),
    intro,
  );
  return {
    root,
    abandon: () => { stopTimer(); call.abandon(); },
  };
}
